import { useState } from "react";
import { timezones } from "@dashboard-v3/shared";
import {
  makeStyles,
  SwitchProps,
  TextField,
  TextFieldProps,
} from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import { SyncScheduleTime } from "@dashboard-v3/api";
import useOrganization from "utils/useOrganization";
import { Autocomplete } from "@material-ui/lab";
import { useTranslation } from "react-i18next";

interface Props {
  value: SyncScheduleTime;
  onChange: (update: SyncScheduleTime | null) => void;
  variant?: TextFieldProps["variant"];
}

type TimeZoneOption = {
  label: string;
  value: string;
};

export default function SyncTimeSelector(props: Props) {
  const { t } = useTranslation("accounts");
  const classes = useStyles();
  const { value, onChange } = props;
  const { organization } = useOrganization();
  const [isEnabled, setIsEnabled] = useState<boolean>(Boolean(value));
  const hours = value?.hours || 6;
  const timeZone = value?.timeZone || organization?.timeZone || "UTC";

  const onToggle: SwitchProps["onChange"] = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    onChange(checked ? { hours, timeZone } : null);
  };

  const onSyncTimeChange: TextFieldProps["onChange"] = e => {
    const { value } = e.target;
    const update: SyncScheduleTime = { hours: Number(value), timeZone };

    onChange(update);
  };

  const onTimeZoneChange = (_, option: string | TimeZoneOption) => {
    const value = (option as TimeZoneOption)?.value;
    const update: SyncScheduleTime = { hours, timeZone: value };

    onChange(update);
  };

  return (
    <div className={classes.mainContainer}>
      <SwitchField
        name="enableSyncSchedule"
        label={t("syncSchedule.enableSyncSchedule.label")}
        description={t("syncSchedule.enableSyncSchedule.desc")}
        value={isEnabled}
        onChange={onToggle}
      />
      {isEnabled && (
        <div className={classes.fieldsContainer}>
          <TextField
            type="number"
            name="hours"
            label={t("syncSchedule.hours.label")}
            value={hours}
            onChange={onSyncTimeChange}
            inputProps={{ min: "1", max: "24" }}
            variant={props.variant || "standard"}
            fullWidth
          />

          <Autocomplete
            options={timezones}
            getOptionLabel={opt => opt.label}
            value={timezones.find(z => z.value === timeZone)}
            onChange={onTimeZoneChange}
            closeIcon={null}
            fullWidth
            getOptionSelected={(option, selected) =>
              option.value === selected.value
            }
            renderInput={inputProps => (
              <TextField
                name="timeZone"
                label={t("syncSchedule.timeZone.label")}
                variant={props.variant || "standard"}
                fullWidth
                {...inputProps}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles({
  mainContainer: { display: "flex", flexDirection: "column", gap: "20px" },
  fieldsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 30,
    alignItems: "center",
  },
});
